import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { adminApi } from "@/lib/api";
import { LIFECYCLE_STAGES, STAGE_BY_ID } from "@/lib/lifecycle";
import { cn } from "@/lib/utils";
import { ArrowRight, Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const tierLabel = { basic: "Bronze", standard: "Silver", premium: "Gold" };

function nextStageId(currentId) {
  const idx = LIFECYCLE_STAGES.findIndex((s) => s.id === currentId);
  if (idx < 0 || idx >= LIFECYCLE_STAGES.length - 1) return null;
  return LIFECYCLE_STAGES[idx + 1].id;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAll = async (status) => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get(
          status && status !== "all"
            ? `/admin/submissions?status=${status}`
            : "/admin/submissions"
        ),
      ]);
      setStats(statsRes.data);
      setSubmissions(listRes.data);
    } catch (_e) {
      /* handled by interceptor */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(statusFilter);
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter(
      (s) =>
        s.full_name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.property_name.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q)
    );
  }, [submissions, search]);

  return (
    <main
      data-testid="admin-dashboard"
      className="max-w-7xl mx-auto px-6 md:px-10 py-10 lg:py-14"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <div className="eyebrow">Studio overview</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl mt-3 text-[#2A1F1A]">
            Host applications
          </h1>
          <p className="mt-3 text-sm text-[#6E6E70] max-w-lg">
            Track every host through application, review, decision and
            onboarding. Open any record to update status, attach documents and
            see the full history.
          </p>
        </div>

        <div className="relative w-full lg:w-80">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E70]"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by host, property, email…"
            data-testid="admin-search-input"
            className="pl-9 bg-white border border-[#E3DED5] focus-visible:border-[#C03028] focus-visible:ring-0 rounded-none h-10"
          />
        </div>
      </div>

      {/* Stage counts */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3 lg:gap-4">
        <StatCard
          label="All applications"
          value={stats?.total ?? "—"}
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
          testid="stat-card-all"
        />
        {LIFECYCLE_STAGES.map((s) => (
          <StatCard
            key={s.id}
            label={s.label}
            value={stats?.by_status?.[s.id] ?? 0}
            active={statusFilter === s.id}
            onClick={() => setStatusFilter(s.id)}
            testid={`stat-card-${s.id}`}
          />
        ))}
      </div>

      {/* Table */}
      <div className="mt-10 bg-white border border-[#E3DED5]">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E3DED5] text-[10px] uppercase tracking-[0.18em] text-[#6E6E70] font-semibold">
          <div className="col-span-3">Host</div>
          <div className="col-span-3">Property</div>
          <div className="col-span-1">Tier</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-[#6E6E70]">
            Loading applications…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="px-6 py-16 text-center text-sm text-[#6E6E70]"
          >
            No applications match this view.
          </div>
        ) : (
          filtered.map((s, idx) => {
            const next = nextStageId(s.status);
            const advance = async () => {
              try {
                await adminApi.patch(`/admin/submissions/${s.id}/status`, {
                  status: next,
                });
                toast.success(`Advanced to ${STAGE_BY_ID[next].label}`);
                fetchAll(statusFilter);
              } catch {
                toast.error("Could not advance status.");
              }
            };
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.02 }}
                data-testid={`admin-row-${s.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-[#E3DED5] last:border-b-0 items-center hover:bg-[#FAF9F6] transition-colors"
              >
                <div className="md:col-span-3">
                  <div className="text-[#2A1F1A] font-medium">{s.full_name}</div>
                  <div className="text-xs text-[#6E6E70]">{s.email}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-[#2A1F1A]">{s.property_name}</div>
                  <div className="text-xs text-[#6E6E70] truncate">
                    {s.address}
                  </div>
                </div>
                <div className="md:col-span-1">
                  <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#B0895C]">
                    {tierLabel[s.tier] || s.tier}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <StatusPill status={s.status} />
                </div>
                <div className="md:col-span-3 flex md:justify-end items-center gap-3">
                  {next && (
                    <button
                      type="button"
                      onClick={advance}
                      data-testid={`admin-row-advance-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#C03028] hover:text-[#9F2620] border border-[#C03028] px-3 py-1.5"
                      title={`Move to ${STAGE_BY_ID[next].label}`}
                    >
                      Advance → {STAGE_BY_ID[next].label}
                      <ChevronRight size={12} />
                    </button>
                  )}
                  <Link
                    to={`/admin/submissions/${s.id}`}
                    data-testid={`admin-row-open-${s.id}`}
                    className="text-sm text-[#C03028] hover:text-[#9F2620] inline-flex items-center gap-1"
                  >
                    Open <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </main>
  );
}

function StatCard({ label, value, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "text-left border p-4 lg:p-5 transition-colors",
        active
          ? "border-[#C03028] bg-white"
          : "border-[#E3DED5] bg-white hover:border-[#B0895C]"
      )}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6E6E70]">
        {label}
      </div>
      <div className="font-serif-display text-3xl mt-2 text-[#2A1F1A]">
        {value}
      </div>
    </button>
  );
}

function StatusPill({ status }) {
  const stage = STAGE_BY_ID[status];
  if (!stage) return <span className="text-xs text-[#6E6E70]">{status}</span>;
  const color =
    status === "onboarding"
      ? "bg-[#C03028] text-[#FAF9F6] border-[#C03028]"
      : status === "decision"
        ? "bg-[#B0895C] text-[#FAF9F6] border-[#B0895C]"
        : status === "in_review"
          ? "bg-[#F2EFE9] text-[#2A1F1A] border-[#E3DED5]"
          : "bg-white text-[#2A1F1A] border-[#E3DED5]";
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={cn(
        "inline-flex items-center px-2.5 py-1 border text-[10px] uppercase tracking-[0.18em] font-semibold",
        color
      )}
    >
      {stage.label}
    </span>
  );
}
